import type { BootstrapConfig } from 'posthog-js';

const KEYS = {
  distinctId: 'ph_distinct_id',
  sessionId: 'ph_session_id',
  isIdentified: 'ph_is_identified',
} as const;

/**
 * Read PostHog bootstrap values from the current page URL (query and hash query).
 * Use the same key names when linking from codec422.com (or any site) so sessions stay joined in PostHog.
 *
 * On the sending site, before navigation:
 *   const u = new URL('https://this-site.example/');
 *   u.searchParams.set('ph_distinct_id', posthog.get_distinct_id());
 *   u.searchParams.set('ph_session_id', posthog.get_session_id());
 *   if (userWasIdentified) u.searchParams.set('ph_is_identified', '1');
 *   location.href = u.toString();
 *
 * If you called posthog.identify(), set ph_is_identified=1 so PostHog treats the distinct id as identified.
 * sessionID must be the same UUID v7 string PostHog issued in that browser (from get_session_id()).
 */
export function readPosthogBootstrapFromUrl(): BootstrapConfig | undefined {
  const sources = getUrlSearchParamSources();
  const distinctID = getFirst(sources, KEYS.distinctId);
  const sessionID = getFirst(sources, KEYS.sessionId);
  const isIdentifiedRaw = getFirst(sources, KEYS.isIdentified);
  const isIdentifiedID =
    isIdentifiedRaw === '1' ||
    isIdentifiedRaw === 'true' ||
    isIdentifiedRaw === 'yes';

  if (!distinctID && !sessionID) {
    return undefined;
  }

  const out: BootstrapConfig = {};
  if (distinctID) {
    out.distinctID = distinctID;
  }
  if (sessionID) {
    out.sessionID = sessionID;
  }
  if (isIdentifiedRaw != null) {
    out.isIdentifiedID = isIdentifiedID;
  }
  return out;
}

/** Remove bootstrap params from the address bar (query and hash) without reloading. */
export function stripPosthogBootstrapParamsFromUrl(): void {
  const url = new URL(window.location.href);
  const toDelete = [KEYS.distinctId, KEYS.sessionId, KEYS.isIdentified];

  toDelete.forEach((k) => url.searchParams.delete(k));

  const h = url.hash;
  if (h.includes('?')) {
    const q = h.indexOf('?');
    const base = h.slice(0, q);
    const params = new URLSearchParams(h.slice(q + 1));
    let changed = false;
    toDelete.forEach((k) => {
      if (params.has(k)) {
        params.delete(k);
        changed = true;
      }
    });
    if (changed) {
      const nq = params.toString();
      url.hash = nq ? `${base}?${nq}` : base;
    }
  }

  if (url.href !== window.location.href) {
    window.history.replaceState({}, '', url.toString());
  }
}

function getUrlSearchParamSources(): URLSearchParams[] {
  const out: URLSearchParams[] = [new URLSearchParams(window.location.search)];
  const hash = window.location.hash;
  const q = hash.indexOf('?');
  if (q >= 0) {
    out.push(new URLSearchParams(hash.slice(q + 1)));
  }
  return out;
}

function getFirst(sources: URLSearchParams[], key: string): string | null {
  for (const s of sources) {
    const v = s.get(key);
    if (v) {
      return v;
    }
  }
  return null;
}
